import centersSeed from "@/data/centers.generated.json";
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { Instagram, Facebook, MapPin, Phone, Mail, MessageCircle, ArrowRight } from "lucide-react";
import { API, FARO_LOGO, COMPANY_EMAIL, whatsappLink, formatMobile } from "@/lib/faro";
import { LANDING_NAV } from "@/data/permitLandings";
const CENTER_PAGE = {
  plazaamerica: "/autoescuela-plaza-america",
  coruxo: "/autoescuela-coruxo",
  panxon: "/autoescuela-panxon",
  nigran: "/autoescuela-nigran",
  acaniza: "/autoescuela-a-caniza",
};

const TIKTOK_URL = "https://www.tiktok.com/@autoescuelasfaro";
const INSTAGRAM_URL = "https://www.instagram.com/autoescuelasfaro/";
const FACEBOOK_URL = "https://www.facebook.com/autoescuelasfaro/?locale=es_ES";

const FooterBrand = () => (
  <div className="lg:col-span-4">
    <div className="flex items-center gap-3">
      <div className="w-12 h-12 rounded-xl overflow-hidden border border-white/20">
        <img src={FARO_LOGO} alt="Autoescuelas Faro" width="48" height="48" loading="lazy" className="w-full h-full object-cover" />
      </div>
      <div className="font-display font-black text-xl">Autoescuelas Faro</div>
    </div>
    <p className="mt-4 text-white/75 max-w-sm">
      Más de 45 años formando conductores en Vigo y su área metropolitana.
      5 centros, una de las flotas más completas del sur de Galicia y un equipo que se preocupa por ti.
    </p>

    <div className="flex items-center gap-3 mt-5">
      <a href={INSTAGRAM_URL} target="_blank" rel="noreferrer" aria-label="Instagram" data-testid="social-instagram" className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 transition flex items-center justify-center">
        <Instagram size={18} />
      </a>
      <a href={FACEBOOK_URL} target="_blank" rel="noreferrer" aria-label="Facebook" data-testid="social-facebook" className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 transition flex items-center justify-center">
        <Facebook size={18} />
      </a>
      <a href={TIKTOK_URL} target="_blank" rel="noreferrer" aria-label="TikTok" data-testid="social-tiktok" className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 transition flex items-center justify-center text-sm font-black">
        TT
      </a>
    </div>

    <div className="mt-6 inline-flex items-center gap-2 text-white/85">
      <Mail size={16} /> <a href={`mailto:${COMPANY_EMAIL}`} className="hover:text-[var(--faro-yellow)]">{COMPANY_EMAIL}</a>
    </div>
  </div>
);

const FooterCenterCard = ({ c, onOpen }) => (
  <div
    data-testid={`footer-center-${c.id}`}
    role="link"
    tabIndex={0}
    onClick={(e) => {
      if (e.target.closest("a")) return;
      onOpen(c.id);
    }}
    onKeyDown={(e) => {
      if (e.key === "Enter") onOpen(c.id);
    }}
    className="rounded-2xl bg-white/5 border border-white/10 p-5 cursor-pointer transition-colors hover:bg-white/10 hover:border-white/25 group"
  >
    <div className="text-xs uppercase tracking-[0.2em] font-bold text-[var(--faro-yellow)] flex items-center justify-between gap-2">
      {c.name}
      <ArrowRight size={13} className="opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
    </div>
    <div className="mt-2 text-sm text-white/85 flex items-start gap-2">
      <MapPin size={14} className="mt-0.5 shrink-0" /> {c.address}
    </div>
    <div className="mt-2 text-sm text-white/85 flex items-center gap-2">
      <Phone size={14} /> <a href={`tel:+34${String(c.phone_landline).replace(/\D/g, "")}`} className="hover:text-[var(--faro-yellow)]">{c.phone_landline}</a>
    </div>
    <a
      href={whatsappLink(c.whatsapp, `Hola, me gustaría información de la sede de ${c.name}`)}
      target="_blank"
      rel="noreferrer"
      data-testid={`footer-whatsapp-${c.id}`}
      className="mt-2 text-sm flex items-center gap-2 font-semibold text-[#25d366] hover:brightness-110"
    >
      <MessageCircle size={14} /> {formatMobile(c.whatsapp)}
    </a>
  </div>
);

const FooterLandings = () => (
  <div className="mt-12 pt-6 border-t border-white/10" data-testid="footer-landings">
    <div className="text-xs uppercase tracking-[0.2em] font-bold text-[var(--faro-yellow)]">Carnets y cursos en Vigo</div>
    <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2 text-sm text-white/75">
      {LANDING_NAV.map((d) => (
        <Link key={d.to} to={d.to} className="hover:text-white" data-testid={`footer-landing${d.to.replace(/\//g, "-")}`}>
          {d.label} en Vigo
        </Link>
      ))}
    </div>
  </div>
);

// Formato "YYYY-MM-DD-HHMM" (build.txt) → "DDMMYYYY.HHMM" (footer)
function formatBuildStamp(raw) {
  const m = String(raw || "").trim().match(/^(\d{4})-(\d{2})-(\d{2})-(\d{2})(\d{2})$/);
  if (!m) return "";
  const [, y, mo, d, hh, mm] = m;
  return `${d}${mo}${y}.${hh}${mm}`;
}

// Lee el sello del propio <meta name="build"> del HTML servido.
// Sin fetch, sin caché intermedia posible: el sello siempre refleja el
// HTML que el navegador acaba de recibir (mismo documento, misma versión).
function readBuildFromMeta() {
  if (typeof document === "undefined") return "";
  const el = document.querySelector('meta[name="build"]');
  return formatBuildStamp(el?.getAttribute("content") || "");
}

const FooterBottom = () => {
  const [stamp, setStamp] = useState("");
  useEffect(() => {
    setStamp(readBuildFromMeta());
  }, []);

  return (
    <div className="mt-8 pt-6 border-t border-white/10 flex flex-col sm:flex-row justify-between items-center gap-3 text-xs text-white/60">
      <div>
        © {new Date().getFullYear()} Automovilística Faro S.L. · CIF B36652170 · Todos los derechos reservados.
        {stamp && (
          <span className="ml-1 text-white/40" data-testid="footer-build-stamp"> {stamp}</span>
        )}
        <div data-testid="footer-dgt-auth">Centro de formación autorizado por la DGT nº 360024</div>
        <div className="mt-1 flex flex-wrap gap-3 justify-center sm:justify-start">
          <Link to="/aviso-legal" className="hover:text-white" data-testid="footer-aviso-legal">Aviso legal</Link>
          <Link to="/politica-privacidad" className="hover:text-white" data-testid="footer-privacidad">Política de privacidad</Link>
          <Link to="/politica-cookies" className="hover:text-white" data-testid="footer-cookies">Política de cookies</Link>
        </div>
      </div>
    <div className="flex items-center gap-4">
      <Link to="/" className="hover:text-white">Inicio</Link>
      <Link to="/permisos" className="hover:text-white">Permisos</Link>
      <Link to="/centros" className="hover:text-white">Centros</Link>
      <Link to="/recuperacion-de-puntos-vigo" className="hover:text-white">Recuperación de puntos Vigo</Link>
      <Link to="/curso-sensibilizacion-vigo" className="hover:text-white">Recuperación del carnet Vigo</Link>
      <Link to="/proximos-cursos" className="hover:text-white">Próximas convocatorias</Link>
      <Link to="/examen" className="hover:text-white" data-testid="footer-examen">Test teórico DGT gratis</Link>
      <Link to="/curso-intensivo-teorica-vigo" className="hover:text-white" data-testid="footer-teorica-intensiva">Intensivo de teórica: teórico en 1 semana</Link>
      <Link to="/blog" className="hover:text-white">Blog</Link>
    </div>
    </div>
  );
};

export default function Footer() {
  const navigate = useNavigate();
  const [centers, setCenters] = useState(Array.isArray(centersSeed) ? centersSeed : []);
  useEffect(() => {
    axios.get(`${API}/centers`).then((r) => setCenters(r.data)).catch(() => {});
  }, []);

  const openCenter = (id) => {
    if (CENTER_PAGE[id]) navigate(CENTER_PAGE[id]);
  };

  return (
    <footer className="bg-[var(--faro-blue-deep)] text-white relative overflow-hidden" data-testid="site-footer">
      <div className="absolute inset-0 faro-noise opacity-25 pointer-events-none" />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <FooterBrand />
          <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {centers.map((c) => (
              <FooterCenterCard key={c.id} c={c} onOpen={openCenter} />
            ))}
          </div>
        </div>

        <FooterLandings />
        <FooterBottom />
      </div>
    </footer>
  );
}
